/**
 * Pure keyboard-shortcut matcher backing the file-browser Cut/Paste + select-all
 * gestures (Drive/Explorer parity, #980).
 *
 * Kept free of React/DOM so the chord→action mapping can be unit-tested in the
 * node-environment vitest suite. The `FileBrowser` keydown handler builds a
 * minimal `KeyEventLike` from the real `KeyboardEvent` (reading whether focus is
 * in an editable target) and dispatches on the returned `ShortcutAction`.
 *
 * Shortcuts:
 *  - Ctrl/Cmd+A → select all items in the current directory
 *  - Ctrl/Cmd+X → cut the current selection to the clipboard
 *  - Ctrl/Cmd+V → paste (= move) the clipboard into the current directory
 *  - Escape     → clear (clipboard first, else selection); needs no modifier
 *
 * All shortcuts are suppressed while the user is typing in an input/textarea/
 * contenteditable, so e.g. Ctrl+A in a text field is not hijacked.
 */

export type ShortcutAction = 'selectAll' | 'cut' | 'paste' | 'clear';

export interface KeyEventLike {
  key: string;
  ctrlKey: boolean;
  metaKey: boolean;
  /** True when focus is in a text input / textarea / contenteditable. */
  inEditableTarget: boolean;
}

/**
 * Map a keyboard event to a file-browser shortcut action, or `null` when no
 * shortcut applies. Returns `null` for any event in an editable target so the
 * browser's native text-editing chords keep working.
 */
export function matchShortcut(e: KeyEventLike): ShortcutAction | null {
  if (e.inEditableTarget) return null;

  if (e.key === 'Escape') return 'clear';

  const mod = e.ctrlKey || e.metaKey;
  if (!mod) return null;

  switch (e.key.toLowerCase()) {
    case 'a':
      return 'selectAll';
    case 'x':
      return 'cut';
    case 'v':
      return 'paste';
    default:
      return null;
  }
}
